import { computed, DestroyRef, inject, Injectable, Signal, signal } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";

import { LktNotificationFilter, LktNotificationView } from "@frontends-v3/design-system";

import { ProfileConstants } from "../../constants/profile.constants";
import { NotificationDto } from "../../models/notifications/notification.interface";
import {
  NotificationChannel,
  NotificationPreferenceGroupDto,
  NotificationPreferencesDto
} from "../../models/notifications/notification-preferences.interface";
import { LektoAuthService } from "../essential/lekto-auth.service";

import { NotificationsService } from "./notifications.service";
import { NotificationsStreamService } from "./notifications-stream.service";

export type NotificationsStatus = "idle" | "loading" | "ready" | "error";

const PAGE_LIMIT = 20;
/** CA 05: no máximo três avisos visíveis ao mesmo tempo. */
const MAX_VISIBLE_TOASTS = 3;
/** CA 01: acima de nove o contador mostra o literal "9+". */
const BADGE_CAP = 9;

const EMPTY_PREFERENCES: NotificationPreferencesDto = { groups: [], criticalAlerts: [] };

/**
 * Ordem canônica dos canais desligados, a mesma que o servidor devolve (ele ordena por `Value`:
 * painel primeiro, e-mail depois). O otimismo reescreve a lista a partir daqui em vez de anexar no
 * fim, para que a lista local nunca divirja em ORDEM da que a próxima carga vai trazer.
 */
const CHANNEL_ORDER: readonly NotificationChannel[] = ["panel", "email"];

/**
 * Estado único do módulo de notificações (DEV-872), compartilhado por platform e admin.
 *
 * Guarda DTO, não view-model: a conversão para `LktNotificationItem` precisa de um `now` e por isso
 * acontece no container, senão o "há 12 min" congelaria no instante da carga.
 *
 * A listagem é a fonte de verdade; o stream é best-effort.
 */
@Injectable({ providedIn: "root" })
export class NotificationsStateService {
  private readonly notificationsService = inject(NotificationsService);
  private readonly streamService = inject(NotificationsStreamService);
  private readonly authService = inject(LektoAuthService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly _items = signal<NotificationDto[]>([]);
  private readonly _nextCursor = signal<string | null>(null);
  private readonly _status = signal<NotificationsStatus>("idle");
  private readonly _loadingMore = signal<boolean>(false);
  private readonly _unreadCount = signal<number>(0);
  private readonly _filter = signal<LktNotificationFilter>("all");
  private readonly _panelOpen = signal<boolean>(false);
  private readonly _view = signal<LktNotificationView>("list");
  private readonly _anchor = signal<HTMLElement | null>(null);
  private readonly _ringToken = signal<number>(0);
  private readonly _toasts = signal<NotificationDto[]>([]);
  private readonly _preferences = signal<NotificationPreferencesDto>(EMPTY_PREFERENCES);
  private readonly _preferencesStatus = signal<NotificationsStatus>("idle");

  public readonly items: Signal<NotificationDto[]> = this._items.asReadonly();
  public readonly status: Signal<NotificationsStatus> = this._status.asReadonly();
  public readonly loadingMore: Signal<boolean> = this._loadingMore.asReadonly();
  public readonly unreadCount: Signal<number> = this._unreadCount.asReadonly();
  public readonly filter: Signal<LktNotificationFilter> = this._filter.asReadonly();
  public readonly panelOpen: Signal<boolean> = this._panelOpen.asReadonly();
  public readonly view: Signal<LktNotificationView> = this._view.asReadonly();
  /** Sino que abriu o painel, para o container ancorar por `getBoundingClientRect()`. */
  public readonly anchor: Signal<HTMLElement | null> = this._anchor.asReadonly();
  /** Incrementa a cada chegada. O rail reinicia a animação quando o número muda. */
  public readonly ringToken: Signal<number> = this._ringToken.asReadonly();
  public readonly toasts: Signal<NotificationDto[]> = this._toasts.asReadonly();
  public readonly preferences: Signal<NotificationPreferencesDto> = this._preferences.asReadonly();
  public readonly preferencesStatus: Signal<NotificationsStatus> =
    this._preferencesStatus.asReadonly();

  /** `false` quando o backend disse que a feature não existe para esta sessão. Esconde o sino. */
  public readonly available: Signal<boolean> = this.streamService.available;

  public readonly hasMore = computed<boolean>(() => this._nextCursor() !== null);

  public readonly badgeLabel = computed<string>(() => {
    const count = this._unreadCount();
    if (count <= 0) {
      return "";
    }
    return count > BADGE_CAP ? `${BADGE_CAP}+` : `${count}`;
  });

  public readonly bellAriaLabel = computed<string>(() => {
    const count = this._unreadCount();
    if (count <= 0) {
      return "Notificações";
    }
    // Minor 1 (fix round final): "1 não lidas" não concorda em número — só quem usa leitor de tela
    // ouvia o bug.
    return `Notificações, ${count} ${count === 1 ? "não lida" : "não lidas"}`;
  });

  private started = false;
  /** Geração da última `loadFirstPage()`. Invalida respostas obsoletas (troca de filtro rápida,
   *  resync concorrente, troca de identidade) que chegariam depois de uma carga mais nova. */
  private loadGeneration = 0;
  /**
   * Geração de identidade. Diferente do `loadGeneration`, que muda a cada carga (troca de filtro,
   * resync), este só muda quando a identidade muda — é o que permite descartar callback em voo da
   * identidade anterior (rollback de markRead/markAllRead/setPreference, carga de preferências,
   * carga do contador) sem cancelar rollback ou carga legítima da identidade corrente.
   */
  private identityGeneration = 0;

  /**
   * Chamado uma vez pelo shell. Idempotente.
   *
   * Minor 4 (fix round final): sem o gate de papel, uma sessão de `aluno` ainda disparava uma
   * requisição de SSE e uma de contador, as duas fadadas a 403 (o backend já nega por papel, e o
   * sino já estava escondido pelo mesmo gate na UI — mas as requisições eram evitáveis). O papel é
   * lido só aqui, no arranque: uma troca de perfil recarrega a página inteira
   * (`globalThis.location.href`), então este serviço — `providedIn: "root"` — nunca sobrevive a uma
   * troca de papel dentro da mesma sessão.
   */
  public init(): void {
    if (this.started) {
      return;
    }
    this.started = true;

    if (!this.isNotificationProfile()) {
      return;
    }

    this.streamService.created
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((dto) => this.onCreated(dto));

    this.streamService.resync
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.onResync());

    this.streamService.identityChanged
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.resetForIdentityChange());

    this.streamService.start();
    this.loadUnreadCount();
  }

  public togglePanel(anchor: HTMLElement): void {
    if (this._panelOpen()) {
      this.closePanel();
      return;
    }

    this._anchor.set(anchor);
    this._panelOpen.set(true);
    // Abrir sempre cai na lista: quem abre o sino quer ver notificação, não a preferência de ontem.
    this._view.set("list");
    // Painel aberto não mostra aviso temporário; a fila viva é descartada (CA 04).
    this._toasts.set([]);
    this.loadFirstPage();
  }

  public closePanel(): void {
    this._panelOpen.set(false);
    this._view.set("list");
  }

  public setFilter(filter: LktNotificationFilter): void {
    if (this._filter() === filter) {
      return;
    }
    this._filter.set(filter);
    // O filtro é do servidor (`unreadOnly`), não do cliente.
    this.loadFirstPage();
  }

  public loadFirstPage(): void {
    this.fetchFirstPage({ silentFailure: false });
  }

  /**
   * Núcleo comum de `loadFirstPage()` e da recarga por resync (`onResync()`), com o guard de
   * geração dos dois handlers preservado nas duas chamadas.
   *
   * `silentFailure` é o que distingue as duas: decisão do dono (contra o comportamento anterior),
   * uma falha na recarga de resync com o painel já mostrando algo estável — lista populada OU
   * vazio legítimo ("você está em dia") — não pode substituir aquela tela por um erro, porque o
   * servidor derruba a conexão SSE de propósito e isso rodaria a cada reconexão. Uma falha na
   * carga inicial continua indo para `"error"`: sem nada em tela ainda, esconder a falha
   * apresentaria uma afirmação que não se sabe se é verdadeira (achado que o `showEmpty()` do
   * container já fecha do lado do template).
   */
  private fetchFirstPage(options: { silentFailure: boolean }): void {
    const generation = ++this.loadGeneration;
    this._status.set("loading");
    this.notificationsService
      .list({ unreadOnly: this._filter() === "unread", limit: PAGE_LIMIT })
      .subscribe({
        next: (page) => {
          // Resposta de uma carga que já foi superada (troca de filtro rápida, resync concorrente,
          // troca de identidade) não pode sobrescrever a lista corrente: o filtro exibido passaria a
          // mentir, ou pior, conteúdo de outra identidade voltaria à tela.
          if (generation !== this.loadGeneration) {
            return;
          }
          this._items.set([...page.items]);
          this._nextCursor.set(page.nextCursor ?? null);
          this._status.set("ready");
        },
        error: () => {
          if (generation !== this.loadGeneration) {
            return;
          }
          if (options.silentFailure) {
            // `_items` e `_nextCursor` não são tocados: a lista (populada ou vazia) e o cursor que
            // já estavam na tela seguem pareados entre si, e o `loadMore()` seguinte continua
            // funcionando a partir do mesmo cursor de antes desta tentativa.
            this._status.set("ready");
            return;
          }
          this._status.set("error");
        }
      });
  }

  public loadMore(): void {
    const cursor = this._nextCursor();
    if (!cursor || this._loadingMore()) {
      return;
    }
    // Página seguinte pertence à geração corrente do filtro. Se uma nova `loadFirstPage()` correr no
    // meio (troca de filtro, resync, troca de identidade), esta página passa a ser de uma sessão já
    // superada.
    const generation = this.loadGeneration;
    this._loadingMore.set(true);

    this.notificationsService
      .list({ unreadOnly: this._filter() === "unread", cursor, limit: PAGE_LIMIT })
      .subscribe({
        next: (page) => {
          // `loadingMore` sempre libera: a chamada terminou de verdade, e travá-la travaria também
          // o próximo `loadMore()` legítimo da sessão nova.
          this._loadingMore.set(false);
          if (generation !== this.loadGeneration) {
            return;
          }
          // Um evento do stream pode ter entrado no topo enquanto a página estava em voo, e o
          // cursor keyset não sabe disso: dedup na concatenação.
          const known = new Set(this._items().map((item) => item.id));
          const fresh = page.items.filter((item) => !known.has(item.id));
          this._items.update((items) => [...items, ...fresh]);
          this._nextCursor.set(page.nextCursor ?? null);
        },
        error: () => this._loadingMore.set(false)
      });
  }

  /** Otimista com rollback. No filtro "Não lidas" o item sai da lista (CA 03). */
  public markRead(id: string): void {
    const previousItems = this._items();
    const previousIndex = previousItems.findIndex((item) => item.id === id);
    const target = previousItems[previousIndex];
    if (!target || target.readAt !== null) {
      return;
    }
    const identity = this.identityGeneration;

    const readAt = new Date().toISOString();

    this._items.set(
      this._filter() === "unread"
        ? previousItems.filter((item) => item.id !== id)
        : previousItems.map((item) => (item.id === id ? { ...item, readAt } : item))
    );
    this._unreadCount.update((count) => Math.max(0, count - 1));

    this.notificationsService.markRead(id).subscribe({
      error: () => {
        // Callback de uma identidade que já não é a corrente não pode tocar em estado nenhum:
        // reinserir o item, mexer no contador ou gravar preferência seria vazar dado entre
        // identidades — exatamente o vazamento que o reset de identidade existe para fechar.
        if (identity !== this.identityGeneration) {
          return;
        }
        // Rollback cirúrgico sobre o estado CORRENTE: um `set` do snapshot apagaria qualquer
        // notificação que tenha chegado pelo stream enquanto o PATCH estava em voo.
        this._items.update((items) => {
          const stillThere = items.some((item) => item.id === id);
          if (stillThere) {
            return items.map((item) => (item.id === id ? { ...item, readAt: null } : item));
          }
          // No filtro "Não lidas" o item foi removido: devolve na posição original.
          const restored = [...items];
          restored.splice(Math.min(previousIndex, restored.length), 0, target);
          return restored;
        });
        this._unreadCount.update((count) => count + 1);
      }
    });
  }

  public markAllRead(): void {
    const previousItems = this._items();
    const affectedIds = previousItems.filter((item) => item.readAt === null).map((item) => item.id);
    const wasUnreadFilter = this._filter() === "unread";
    const identity = this.identityGeneration;
    // Contador CHEIO antes do `set(0)`, não o número de itens afetados: `affectedIds` só cobre a
    // página carregada (até `PAGE_LIMIT`), enquanto `_unreadCount` pode refletir muito mais não
    // lidas no servidor. Rolar de volta usando `affectedIds.length` devolveria só a página, não o
    // total — o bug ficava invisível porque todo teste existente carregava exatamente tantos itens
    // quanto não lidas.
    const previousCount = this._unreadCount();
    const readAt = new Date().toISOString();

    this._items.set(
      wasUnreadFilter ? [] : previousItems.map((item) => (item.readAt ? item : { ...item, readAt }))
    );
    this._unreadCount.set(0);

    this.notificationsService.markAllRead().subscribe({
      error: () => {
        // Callback de uma identidade que já não é a corrente não pode tocar em estado nenhum.
        if (identity !== this.identityGeneration) {
          return;
        }
        // Depois do `set(0)` acima, `count` só guarda o que chegou por stream nesse meio-tempo (o
        // mesmo delta que motiva o rollback de itens por delta, nunca por snapshot). Somar
        // `previousCount` devolve o total exato, chegado ou não pelo stream.
        this._unreadCount.update((count) => count + previousCount);
        if (wasUnreadFilter) {
          // No filtro "Não lidas" a lista foi esvaziada otimisticamente: não há como reconstruir por
          // delta (perdemos ordem e cursor). Recarrega do servidor em vez de tentar remontar — e o
          // contador junto, pela mesma razão: já estamos indo à fonte de verdade para a lista.
          this.loadFirstPage();
          this.loadUnreadCount();
          return;
        }
        // Rollback por delta, nunca por snapshot: reverte só os ids que ESTA chamada marcou, para
        // não desfazer uma notificação chegada pelo stream enquanto o PATCH estava em voo.
        const affected = new Set(affectedIds);
        this._items.update((items) =>
          items.map((item) => (affected.has(item.id) ? { ...item, readAt: null } : item))
        );
      }
    });
  }

  public showPreferences(): void {
    this._view.set("preferences");
    this.loadPreferences();
  }

  /**
   * Abre o painel JÁ na vista de preferências, sem clique no sino — é o destino dos dois links do
   * rodapé do e-mail (`?notificacoes=preferencias`, DEV-877). Não confundir com `togglePanel()`,
   * que sempre cai na lista: ali quem abriu quer ver notificação, aqui quem chegou quer calibrar o
   * que recebe.
   *
   * Sem âncora: quem posiciona é o container, que resolve o sino visível quando o painel não foi
   * aberto por clique.
   *
   * O gate é o PAPEL, e não `available()`: o papel é síncrono e determinístico, enquanto
   * `available()` só fica falso depois de o SSE responder — no instante do deep link ele ainda não
   * respondeu, e gatear nele descartaria a intenção por corrida. Se a feature de fato não existir
   * para a sessão, a carga falha e a vista mostra o estado de erro dela, que não mente.
   */
  public openPreferences(): void {
    if (!this.isNotificationProfile()) {
      return;
    }

    this._panelOpen.set(true);
    // Painel aberto não mostra aviso temporário; a fila viva é descartada (CA 04).
    this._toasts.set([]);
    this._view.set("preferences");
    this.loadPreferences();
    // A lista carrega TAMBÉM: o botão "voltar" desta vista só troca a vista, e sem esta carga ele
    // cairia num corpo em branco — `status` ficaria em "idle", que não é vazio legítimo nem erro,
    // e nenhum dos dois ramos do template renderiza copy nenhuma.
    this.loadFirstPage();
  }

  /**
   * Botão "tentar novamente" da vista de preferências (achado 2, Important, fix round final):
   * mesmo achado do painel de lista, verificado também aqui — `preferencesStatus` já existia e era
   * testado, mas nenhum template de produção o lia, e a lista vazia por erro se apresentava como
   * "Nada para configurar ainda".
   */
  public retryPreferences(): void {
    this.loadPreferences();
  }

  public showList(): void {
    this._view.set("list");
  }

  /**
   * Um interruptor por chamada — e um CANAL por chamada (DEV-877): mexer no painel nunca toca o
   * e-mail, nem no otimismo local nem no rollback. Otimista com rollback; 422 = chave fora do papel
   * corrente ou canal desconhecido.
   *
   * `disabled` é a polaridade do CONTRATO, não da tela: quem inverte a chavinha em desligamento é o
   * container, no mesmo lugar (e uma vez só) em que inverte na leitura.
   */
  public setPreference(toggleKey: string, channel: NotificationChannel, disabled: boolean): void {
    const previous = this._preferences();
    const identity = this.identityGeneration;
    this._preferences.set({
      groups: this.applyToggle(previous.groups, toggleKey, channel, disabled),
      criticalAlerts: this.applyToggle(previous.criticalAlerts, toggleKey, channel, disabled)
    });

    this.notificationsService.setPreference({ toggleKey, channel, disabled }).subscribe({
      error: () => {
        // Callback de uma identidade que já não é a corrente não pode tocar em estado nenhum: o
        // `toggleKey` é chave estável de papel/produto, não por usuário — a colisão entre B e A é
        // plausível, e reverter aqui inverteria um toggle que o servidor nunca alterou para B.
        if (identity !== this.identityGeneration) {
          return;
        }
        // Reverte só esta chave NESTE canal: um `set` do snapshot desfaria toggles concorrentes que
        // o servidor já persistiu — inclusive o do outro canal da MESMA chave, que é o par mais
        // fácil de tocar em sequência (a pessoa desliga a linha inteira com dois cliques).
        this._preferences.update((current) => ({
          groups: this.applyToggle(current.groups, toggleKey, channel, !disabled),
          criticalAlerts: this.applyToggle(current.criticalAlerts, toggleKey, channel, !disabled)
        }));
      }
    });
  }

  public dismissToast(id: string): void {
    this._toasts.update((toasts) => toasts.filter((toast) => toast.id !== id));
  }

  private loadPreferences(): void {
    // Sem guarda nenhuma antes: uma resposta da identidade anterior gravava preferência de A em
    // cima do estado (já resetado) de B, e ainda deixava `preferencesStatus = "ready"` mentindo.
    const identity = this.identityGeneration;
    this._preferencesStatus.set("loading");
    this.notificationsService.getPreferences().subscribe({
      next: (preferences) => {
        if (identity !== this.identityGeneration) {
          return;
        }
        this._preferences.set(preferences);
        this._preferencesStatus.set("ready");
      },
      error: () => {
        if (identity !== this.identityGeneration) {
          return;
        }
        this._preferencesStatus.set("error");
      }
    });
  }

  private loadUnreadCount(): void {
    // Mesma classe de vazamento dos rollbacks acima, achada ao implementar o `identityGeneration`:
    // uma chamada em voo da identidade anterior (ex.: disparada por um resync um instante antes da
    // troca) pode resolver DEPOIS do `loadUnreadCount()` que este reset acabou de disparar para a
    // identidade nova, sobrescrevendo o contador certo de B com o valor antigo de A.
    const identity = this.identityGeneration;
    this.notificationsService.getUnreadCount().subscribe((result) => {
      if (identity !== this.identityGeneration) {
        return;
      }
      // `null` = a chamada falhou. Manter o contador anterior é melhor que exibir zero falso: um
      // badge vazio afirma "nada novo", e isso é pior que um badge levemente desatualizado.
      if (result) {
        this._unreadCount.set(result.unreadCount);
      }
    });
  }

  private onCreated(dto: NotificationDto): void {
    // Dedup por id: o evento pode chegar com a listagem em voo, e várias abas do mesmo produto
    // recebem o mesmo evento com o mesmo id.
    if (this._items().some((item) => item.id === dto.id)) {
      return;
    }

    // No filtro "Não lidas" só entra o que está não lido — e tudo que chega por evento está.
    if (this._filter() === "all" || dto.readAt === null) {
      this._items.update((items) => [dto, ...items]);
    }
    if (dto.readAt === null) {
      this._unreadCount.update((count) => count + 1);
    }
    this._ringToken.update((token) => token + 1);

    if (!this._panelOpen()) {
      this.enqueueToast(dto);
    }
  }

  private enqueueToast(dto: NotificationDto): void {
    this._toasts.update((toasts) => {
      const next = [...toasts, dto];
      // Teto de três: os mais antigos saem. Nada se perde — todos seguem no painel.
      return next.length > MAX_VISIBLE_TOASTS ? next.slice(next.length - MAX_VISIBLE_TOASTS) : next;
    });
  }

  private onResync(): void {
    // Não há replay por Last-Event-ID: reconectar significa recarregar contador e, se a lista já
    // foi carregada alguma vez, a primeira página também.
    this.loadUnreadCount();
    if (this._status() === "idle") {
      return;
    }
    // Decisão do dono: falha nesta recarga só pode ficar silenciosa (mantendo a tela como estava)
    // quando já havia algo estável para manter — isto é, a última carga desta geração de filtro
    // chegou a "ready". Se o painel ainda estava "loading" (uma carga anterior em voo) ou já
    // "error" (uma recarga de resync anterior que falhou), não há tela estável para preservar, e
    // esta falha se comporta como a de uma carga inicial.
    this.fetchFirstPage({ silentFailure: this._status() === "ready" });
  }

  /**
   * Troca de identidade descarta TODO o conteúdo da identidade anterior. O canal SSE já é derrubado
   * e reaberto pelo stream service pelo mesmo motivo; sem esta contraparte, lista e avisos da
   * identidade anterior seguiriam na tela — inaceitável num produto que trata dado de menor de idade.
   */
  private resetForIdentityChange(): void {
    // Dois contadores, dois vazamentos diferentes que este reset fecha:
    // - `loadGeneration` invalida `loadFirstPage()`/`loadMore()` em voo (a lista da tela).
    // - `identityGeneration` invalida o rollback de markRead/markAllRead/setPreference e a carga
    //   de preferências/contador em voo — sem ele, um PATCH ou GET da identidade anterior que
    //   resolve DEPOIS deste reset reintroduziria o dado (ou o número) que acabamos de descartar.
    this.loadGeneration++;
    this.identityGeneration++;
    this._items.set([]);
    this._nextCursor.set(null);
    this._status.set("idle");
    this._loadingMore.set(false);
    this._toasts.set([]);
    this._unreadCount.set(0);
    this._preferences.set(EMPTY_PREFERENCES);
    this._preferencesStatus.set("idle");
    this._filter.set("all");
    this._anchor.set(null);
    // Minor 6 (fix round final): faltava aqui, contradizendo o próprio doc-comment deste método
    // ("descarta TODO o conteúdo"). Inofensivo hoje (só o rail do sino reage a mudança dele), mas
    // inconsistente com a invariante declarada.
    this._ringToken.set(0);
    this.closePanel();
    this.loadUnreadCount();
  }

  private isNotificationProfile(): boolean {
    return ProfileConstants.NOTIFICATION_PROFILES.includes(this.authService.authProfile());
  }

  /**
   * Grava o desligamento de UM canal na linha da chave, espelhando a escrita do servidor: entrar na
   * lista é `AddToSet` e sair é `Pull`, as duas idempotentes. Por isso o conjunto é reconstruído a
   * partir de `CHANNEL_ORDER` — repetir o mesmo toque não duplica canal, e a ordem local continua
   * igual à da resposta.
   */
  private applyToggle(
    rows: NotificationPreferenceGroupDto[],
    toggleKey: string,
    channel: NotificationChannel,
    disabled: boolean
  ): NotificationPreferenceGroupDto[] {
    return rows.map((row) => {
      if (row.toggleKey !== toggleKey) {
        return row;
      }

      const disabledChannels = new Set(row.disabledChannels);
      if (disabled) {
        disabledChannels.add(channel);
      } else {
        disabledChannels.delete(channel);
      }

      return {
        ...row,
        disabledChannels: CHANNEL_ORDER.filter((item) => disabledChannels.has(item))
      };
    });
  }
}
